import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faCode, faFont, faGlobe, faImage, faMusic, faPlay } from "@fortawesome/free-solid-svg-icons";
import { csrfToken } from "../../../../../../../utils/csrfTokenUtils";
import { EditorInfoContext } from "../../../../../../../utils/userInfoContext";
import CustomInput from "../../../../../../CommonComponents/CustomForm/CustomInputField";
import { networkService } from "../../../../../../config/axios";
import Button from "../../../../../../Elements/Button";
import useEditorHook from "../../../editorHook/useEditorHook";
import SectionCard from "../../SectionCard";
import EmbedSection from "../embedSection/EmbedSection";
import ImageSection from "../imageSection/ImageSection";
import TextEditorSection from "../textEditorSection/TextEditorSection";
import HtmlSourceSection from "./HtmlSourceSection";
import VideoSection from "../videoSection/VideoSection";
import ImageSlider from "./ImageSlider";
import LightBoxSlideList from "./LightBoxSlideList";
import { DEFAULT_SLIDE_INTERVAL } from "./LightBoxSlideshow";
import LightBoxElement, { typeIdFor } from "./LightBoxElement";
import LightBoxExtendWarningModal from "./LightBoxExtendWarningModal";
import LightBoxTrigger, { TRIGGERS } from "./LightBoxTrigger";
import PreviewSizeBlock from "../../PreviewSizeBlock";
import LightBoxUsages, { usageText } from "./LightBoxUsages";

const FRAME_TYPES = [5, 6, 8];
const RATIOS = { '16:9': 16 / 9, '4:3': 4 / 3, '1:1': 1 };
const TYPE_ICONS = { 4: faImage, 5: faPlay, 6: faGlobe, 7: faFont, 8: faMusic, 12: faCode };
const slideOrder = (slide) => slide?.order_on_page ?? slide?.id;
const bySlideOrder = (a, b) => slideOrder(a) - slideOrder(b) || a.id - b.id;

const LightBoxSetting = ({ docFileId, docId, setKey, handleClose }) => {
    const { editLightBox, imageElement, setImageElement, setShowToast, setToolType } = useContext(EditorInfoContext)
    const docData = {
        doc_id: docId,
        id: docFileId
    }
    const { tool, handleElementUpload, uploadElement, setTool, editImageInSlider, handleTextChange, handleHtmlChange, handleImageUpload, handleSelectGifs, errorState, editElement } = useEditorHook(null, docData)
    const inputRef = useRef(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setTool(prev => ({
            ...prev,
            geometry: { ...prev.geometry, left: 0, top: 0 }
        }));
    }, []);

    const initialState = {
        authenticity_token: csrfToken(),
        display: "click",
        doc_id: docId,
        docs_file_id: docFileId,
        height: 300,
        name: '',
        page: 1,
        show_after: '',
        show_on_page: "",
        show_slide_desc: "",
        slide_interval: DEFAULT_SLIDE_INTERVAL,
        slide_show: false,
        width: 300,
        keep_proportions: true
    }

    const [lightBoxPayload, setLightBoxPayload] = useState(initialState)

    const lastValidSizeRef = useRef({ width: initialState.width, height: initialState.height });
    const [showSizeWarning, setShowSizeWarning] = useState(false);

    const savedElements = useMemo(() => Object.values(editLightBox?.elements ?? {}), [editLightBox]);
    const savedType = savedElements[0]?.element_type;

    const handleSelectGifsForLightbox = (type, url, dimensions) => {
        setDirty(true);
        handleSelectGifs(type, url, dimensions);
        if (dimensions?.width && dimensions?.height) {
            setLightBoxPayload(prev => ({
                ...prev,
                width: Math.round(dimensions.width),
                height: Math.round(dimensions.height)
            }));
        }
    };

    const [lbEditElement, setLbEditElement] = useState({})

    const handleImageUploadForLightbox = (e, action) => {
        setDirty(true);
        handleImageUpload(e, action);
        if (action === 'cancel') {
            setLightBoxPayload(prev => ({ ...prev, width: 300, height: 300 }));
            lastValidSizeRef.current = { width: 300, height: 300 };
            setLbEditElement({});
            setTool(prev => ({ ...prev, id: undefined }));
        }
    };
    const [slidePickerKey, setSlidePickerKey] = useState(0);
    const [addingSlide, setAddingSlide] = useState(false);
    const [replacingSlide, setReplacingSlide] = useState(null);

    const [elementName, setElementName] = useState('')
    const [error, setError] = useState({
        fieldError: '',
        elementError: '',
        contentError: '',
        sizeError: ''
    })
    const errorTimerRef = useRef(null)

    useEffect(() => {
        if (error?.fieldError || error?.contentError || error?.sizeError || error?.elementError) {
            clearTimeout(errorTimerRef.current);
            errorTimerRef.current = setTimeout(() => {
                setError({ fieldError: '', elementError: '', contentError: '', sizeError: '' });
            }, 5000);
        }
        return () => clearTimeout(errorTimerRef.current);
    }, [error?.fieldError, error?.contentError, error?.sizeError, error?.elementError]);

    const getElementType = (data) => {
        const first = data ? Object.values(data)[0] : null;
        return first ? { element_type: first.element_type, value: first.value } : undefined;
    }

    const chosenType = elementName ? typeIdFor(elementName) : getElementType(lightBoxPayload?.elements)?.element_type;
    const isImageMode = chosenType === 4;
    const hasElement = !!chosenType;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setDirty(true);
        setError({
            elementError: '',
            fieldError: '',
            contentError: '',
            sizeError: ''
        })
        if (name === 'element') {
            setLightBoxPayload((prev) => {
                const { elements, ...rest } = prev;
                const isNewFrame = !editLightBox?.id && FRAME_TYPES.includes(typeIdFor(value))
                    && prev.width === initialState.width && prev.height === initialState.height;
                if (isNewFrame) lastValidSizeRef.current = { width: 640, height: 360 };
                return {
                    ...rest,
                    ...(isNewFrame && { width: 640, height: 360 }),
                    [name]: type === 'checkbox' ? checked : value,
                };
            });
            setElementName(value)
            if (value === 'image') {
                setToolType('LightboxesToolbox');
            } else {
                setToolType('');
            }
        } else if (name === 'width' || name === 'height') {
            const parsedValue = value === '' ? '' : Number(value);

            let newWidth = name === 'width' ? parsedValue : lightBoxPayload.width;
            let newHeight = name === 'height' ? parsedValue : lightBoxPayload.height;

            if (lightBoxPayload.keep_proportions && parsedValue !== '') {
                const ratioSource = (isImageMode && tool?.original_width && tool?.original_height)
                    ? { w: tool.original_width, h: tool.original_height }
                    : (isImageMode && lbEditElement?.file_details?.dimensions?.width && lbEditElement?.file_details?.dimensions?.height)
                        ? { w: lbEditElement.file_details.dimensions.width, h: lbEditElement.file_details.dimensions.height }
                        : (lightBoxPayload.width && lightBoxPayload.height)
                            ? { w: lightBoxPayload.width, h: lightBoxPayload.height }
                            : null;

                if (ratioSource) {
                    const aspectRatio = ratioSource.w / ratioSource.h;
                    if (name === 'width') {
                        newHeight = Math.round(parsedValue / aspectRatio);
                    } else {
                        newWidth = Math.round(parsedValue * aspectRatio);
                    }
                }
            }

            setLightBoxPayload(prev => ({ ...prev, width: newWidth, height: newHeight }));
            setTool(prev => ({
                ...prev,
                geometry: { ...prev.geometry, width: newWidth, height: newHeight }
            }));

            const isNumericSize = typeof newWidth === 'number' && typeof newHeight === 'number';
            const exceedsWindow = isNumericSize && (newWidth > window.innerWidth || newHeight > window.innerHeight);

            if (exceedsWindow) {
                setShowSizeWarning(true);
            } else if (isNumericSize) {
                lastValidSizeRef.current = { width: newWidth, height: newHeight };
            }
        } else {
            setLightBoxPayload(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
            if (name === 'slide_show' && checked && !hasPendingSlide) resetSlidePicker();
        }
    }

    const markDirty = (handler) => (...args) => {
        setDirty(true);
        return handler(...args);
    };

    const handleConfirmOversizedLightbox = () => {
        lastValidSizeRef.current = { width: lightBoxPayload.width, height: lightBoxPayload.height };
    };

    const handleRevertOversizedLightbox = () => {
        const { width, height } = lastValidSizeRef.current;
        setLightBoxPayload(prev => ({ ...prev, width, height }));
        setTool(prev => ({
            ...prev,
            geometry: { ...prev.geometry, width, height }
        }));
    };

    const naturalSize = tool?.original_width && tool?.original_height
        ? { width: tool.original_width, height: tool.original_height }
        : lbEditElement?.file_details?.dimensions;

    const applySizePreset = (label) => {
        const w = Number(lightBoxPayload.width) || 0;
        const h = Number(lightBoxPayload.height) || 0;
        let nw = w;
        let nh = h;
        let unlock = false;
        if (RATIOS[label] && w > 0) {
            nh = Math.round(w / RATIOS[label]);
            unlock = true;
        } else if (label === 'Fit content' && naturalSize?.width && naturalSize?.height) {
            nw = Math.round(naturalSize.width);
            nh = Math.round(naturalSize.height);
        } else {
            return;
        }
        setDirty(true);
        setLightBoxPayload(prev => ({ ...prev, width: nw, height: nh, ...(unlock && { keep_proportions: false }) }));
        setTool(prev => ({ ...prev, geometry: { ...prev.geometry, width: nw, height: nh } }));
        if (nw > window.innerWidth || nh > window.innerHeight) {
            setShowSizeWarning(true);
        } else {
            lastValidSizeRef.current = { width: nw, height: nh };
        }
    };

    const isInvalidSize = (value) => value === '' || Number(value) <= 0;

    const handleSizeBlur = () => {
        const widthInvalid = isInvalidSize(lightBoxPayload.width);
        const heightInvalid = isInvalidSize(lightBoxPayload.height);
        if (!widthInvalid && !heightInvalid) return;
        const { width, height } = lastValidSizeRef.current;
        setLightBoxPayload(prev => ({
            ...prev,
            width: widthInvalid ? width : prev.width,
            height: heightInvalid ? height : prev.height
        }));
        setTool(prev => ({
            ...prev,
            geometry: {
                ...prev.geometry,
                width: widthInvalid ? width : lightBoxPayload.width,
                height: heightInvalid ? height : lightBoxPayload.height
            }
        }));
    };

    useEffect(() => {
        if (!isImageMode || !tool?.original_width || !tool?.original_height) return;
        if (lightBoxPayload?.slide_show && arrayOfImageElements.length > 0) return;
        const newWidth = Math.round(tool?.geometry?.width ?? tool.original_width);
        const newHeight = Math.round(tool?.geometry?.height ?? tool.original_height);
        setLightBoxPayload(prev => ({
            ...prev,
            width: newWidth,
            height: newHeight
        }));
        if (newWidth > window.innerWidth || newHeight > window.innerHeight) {
            setShowSizeWarning(true);
        } else {
            lastValidSizeRef.current = { width: newWidth, height: newHeight };
        }
    }, [tool?.original_width, tool?.original_height]);

    const patchObjInitLightBox = (lb) => {
        if (!lb?.group_id) return;
        if (!window.objInit) window.objInit = {};
        if (!window.objInit.lightBoxes) window.objInit.lightBoxes = {};
        Object.values(window.objInit.lightBoxes).forEach((bucket) => {
            if (bucket && typeof bucket === 'object') delete bucket[lb.group_id];
        });
        const display = lb.display || 'click';
        if (!window.objInit.lightBoxes[display]) window.objInit.lightBoxes[display] = {};
        window.objInit.lightBoxes[display][lb.group_id] = lb;
    };

    const refreshObjInitLightBox = async (id) => {
        const res = await networkService(`docs/lightboxes/${id}.json`, 'GET');
        if (res?.status === 200) patchObjInitLightBox(res.data);
    };

    const sizedGeometry = (geometry) => ({
        ...(geometry || {}),
        width: Number(lightBoxPayload.width),
        height: Number(lightBoxPayload.height),
    });

    const associateImages = async (lightboxId, freshElements) => {
        const elementsToAssociate = [...(freshElements ?? arrayOfImageElements)].sort(bySlideOrder);
        const results = await Promise.all(elementsToAssociate.map((item, index) =>
            editImageInSlider(item?.id, { ...item, docs_lightbox_id: lightboxId, order_on_page: index, geometry: sizedGeometry(item?.geometry) })
        ));
        if (!results.every(r => r?.status === 200)) throw new Error('Element association failed');
    };

    const createContentElement = (lightboxId) => networkService('docs/elements.json', 'POST', {
        doc_id: docId,
        docs_file_id: docFileId,
        docs_lightbox_id: lightboxId,
        element_type: chosenType,
        value: tool?.value,
        geometry: {
            ...sizedGeometry(),
            top: 0, left: 0,
            keep_proportions: true,
            keep_within_page: true,
            hide_beyond_page_edges: false
        },
    });

    const removeElements = (elements) => Promise.all(
        elements.map((el) => networkService(`docs/elements/${el.id}.json`, 'DELETE'))
    );

    const finishSave = async (lightboxId, type) => {
        await refreshObjInitLightBox(lightboxId);
        setImageElement({})
        setDirty(false);
        if (type == 'save') setKey('list');
        else if (type == 'close') handleClose();
    };

    const errorToast = (message) => setShowToast({ showToast: true, toastType: 'danger', toastMessage: message });

    const createLightBox = async (type, freshElements = null) => {
        let lightBoxData;
        try {
            lightBoxData = await networkService(`docs/lightboxes.json`, 'POST', lightBoxPayload);
        } catch (err) {
            errorToast("Something went wrong");
            return;
        }
        patchObjInitLightBox(lightBoxData.data);
        setShowToast({ showToast: true, toastType: 'success', toastMessage: "Lightbox saved." })
        setTool({
            ...tool,
            doc_id: docId,
            docs_file_id: docFileId,
            docs_lightbox_id: lightBoxData?.data?.id
        })
        try {
            if (isImageMode) {
                await associateImages(lightBoxData.data.id, freshElements);
            } else {
                await createContentElement(lightBoxData.data.id);
            }
            await finishSave(lightBoxData.data.id, type);
        } catch (err) {
            errorToast("Lightbox saved, but its content couldn't be saved. Please try again.");
        }
    };

    const updateLightBox = async (type, freshElements = null) => {
        let lightBoxData;
        try {
            lightBoxData = await networkService(`docs/lightboxes/${editLightBox?.id}.json`, 'PATCH', lightBoxPayload);
        } catch (err) {
            errorToast("Something went wrong");
            return;
        }
        patchObjInitLightBox(lightBoxData.data);
        setTool({
            ...tool,
            doc_id: docId,
            docs_file_id: docFileId,
            docs_lightbox_id: lightBoxData?.data?.id
        })
        setShowToast({ showToast: true, toastType: 'success', toastMessage: "Lightbox updated" })
        const typeChanged = savedElements.length > 0 && chosenType !== savedType;
        try {
            if (isImageMode) {
                if (typeChanged) await removeElements(savedElements);
                await associateImages(lightBoxData.data.id, freshElements);
            } else if (typeChanged || savedElements.length === 0) {
                if (typeChanged) await removeElements(savedElements);
                await createContentElement(lightBoxData.data.id);
            } else {
                const existingElem = savedElements[0];
                await networkService(`docs/elements/${existingElem.id}.json`, 'PATCH', {
                    doc_id: docId,
                    docs_file_id: docFileId,
                    element_type: chosenType,
                    value: tool?.value,
                    geometry: sizedGeometry(existingElem?.geometry),
                });
            }
            await finishSave(lightBoxData.data.id, type);
        } catch (err) {
            errorToast("Lightbox updated, but its content couldn't be saved. Please try again.");
        }
    }

    const getContentHasValue = () => {
        if (!hasElement) return true;
        if (isImageMode) {
            return !!(tool?.file || tool?.value?.gif_link || arrayOfImageElements.length > 0);
        }
        if (FRAME_TYPES.includes(chosenType)) {
            return !!tool?.value?.video_link;
        }
        if ([7, 12].includes(chosenType)) {
            return !!tool?.value?.html_text;
        }
        return true;
    };

    const handleLightBox = async (type) => {
        if (saving) return;
        if (!lightBoxPayload?.name) {
            setError({ ...error, fieldError: 'Please add the lightbox name', contentError: '' })
        } else if (!hasElement) {
            setError({ ...error, elementError: 'Please select a content type', contentError: '' })
        } else if (!getContentHasValue()) {
            setError({ ...error, fieldError: '', elementError: '', contentError: 'Please add content for the selected element' })
        } else if (isInvalidSize(lightBoxPayload.width) || isInvalidSize(lightBoxPayload.height)) {
            setError({ ...error, sizeError: 'Width and height must be greater than 0', fieldError: '', contentError: '' })
        } else {
            setSaving(true);
            try {
                let freshElements = null;
                if (isSlideshow && hasPendingSlide) {
                    const uploaded = await uploadElement(null, 'image', true);
                    if (!uploaded) return;
                    const placed = replacingSlide ? { ...uploaded, order_on_page: slideOrder(replacingSlide) } : uploaded;
                    freshElements = [...arrayOfImageElements.filter(el => el.id !== uploaded.id && el.id !== replacingSlide?.id), placed];
                    if (replacingSlide) {
                        await removeElements([replacingSlide]);
                        setReplacingSlide(null);
                    }
                } else if (isSlideshow) {
                    freshElements = null;
                } else if (isImageMode && (tool?.file || tool?.value?.gif_link)) {
                    const existingElemId = arrayOfImageElements.length > 0 ? Object.keys(imageElement)[0] : null;
                    const uploaded = await uploadElement(existingElemId, 'image', true);
                    if (!uploaded) return;
                    freshElements = [uploaded];
                }
                if (editLightBox?.id) {
                    await updateLightBox(type, freshElements)
                } else {
                    await createLightBox(type, freshElements)
                }
            } finally {
                setSaving(false);
            }
        }
    }

    useEffect(() => {
        if (editLightBox?.id) {
            const existingElem = Object.values(editLightBox?.elements ?? {})[0];
            const shownSize = FRAME_TYPES.includes(existingElem?.element_type)
                && editLightBox.width === initialState.width && editLightBox.height === initialState.height
                ? { width: 854, height: 480 }
                : { width: editLightBox.width, height: editLightBox.height };
            setLightBoxPayload({ keep_proportions: true, ...editLightBox, ...shownSize, slide_interval: editLightBox.slide_interval || DEFAULT_SLIDE_INTERVAL })
            lastValidSizeRef.current = shownSize;
            if (existingElem?.element_type === 4) {
                setImageElement(editLightBox?.elements)
                setElementName('image')
                setToolType('LightboxesToolbox')
                setLbEditElement(existingElem);
                if (!editLightBox.slide_show) setTool(prev => ({
                    ...prev,
                    id: existingElem.id,
                    gif_type: existingElem.gif_type || null,
                    is_tenor: existingElem.is_tenor ?? false,
                    geometry: {
                        ...prev.geometry,
                        width: editLightBox.width,
                        height: editLightBox.height,
                    },
                    value: {
                        ...prev.value,
                        ...(existingElem.value?.gif_link && { gif_link: existingElem.value.gif_link }),
                        ...(existingElem.value?.unsplash_id && {
                            unsplash_id: existingElem.value.unsplash_id,
                            unsplash_user_name: existingElem.value.unsplash_user_name,
                            unsplash_user_link: existingElem.value.unsplash_user_link,
                        }),
                    },
                }));
            } else if (existingElem) {
                setTool(prev => ({
                    ...prev,
                    element_type: existingElem.element_type,
                    value: { ...prev.value, ...(existingElem.value || {}) },
                }));
            }
        } else {
            setImageElement({})
            setLbEditElement({})
            setLightBoxPayload({ ...initialState })
            lastValidSizeRef.current = { width: initialState.width, height: initialState.height };
            setToolType('')
        }
    }, [editLightBox])

    useEffect(() => {
        return () => setToolType('');
    }, []);

    const arrayOfImageElements = useMemo(() => {
        return Object.values(
            Object.entries(imageElement).length !== 0 ? imageElement : {}
        );
    }, [imageElement]);

    const isSlideshow = isImageMode && !!lightBoxPayload?.slide_show;
    const hasPendingSlide = !!tool?.file ||
        (!!tool?.value?.gif_link && !arrayOfImageElements.some(el => el?.value?.gif_link === tool.value.gif_link));

    const resetSlidePicker = () => {
        handleImageUpload(null, 'cancel');
        setTool(prev => ({ ...prev, id: undefined }));
        setSlidePickerKey(k => k + 1);
    };

    useEffect(() => {
        if (replacingSlide && (!isSlideshow || !imageElement[replacingSlide.id])) setReplacingSlide(null);
    }, [replacingSlide, isSlideshow, imageElement]);

    const handleEditSlide = (slide, number) => {
        resetSlidePicker();
        setReplacingSlide({ ...slide, number });
    };

    const handleAddSlide = async () => {
        if (!hasPendingSlide || addingSlide) return;
        setAddingSlide(true);
        const replacing = replacingSlide;
        try {
            const uploaded = await uploadElement(null, 'image', true);
            if (!uploaded) return;
            let slide = replacing ? { ...uploaded, order_on_page: slideOrder(replacing) } : uploaded;
            if (editLightBox?.id) {
                const [elRes, lbRes] = await Promise.all([
                    editImageInSlider(uploaded.id, { ...uploaded, docs_lightbox_id: editLightBox.id, order_on_page: replacing ? slideOrder(replacing) : arrayOfImageElements.reduce((max, el) => Math.max(max, Number(el?.order_on_page) || 0), -1) + 1 }),
                    networkService(`docs/lightboxes/${editLightBox.id}.json`, 'PATCH', {
                        authenticity_token: csrfToken(),
                        slide_show: true,
                        slide_interval: lightBoxPayload?.slide_interval || DEFAULT_SLIDE_INTERVAL,
                    }),
                ]);
                if (elRes?.status !== 200 || lbRes?.status !== 200) {
                    setShowToast({ showToast: true, toastType: 'danger', toastMessage: "Slide uploaded, but it couldn't be added to the lightbox. Please Save to retry." });
                    setImageElement(prev => ({ ...prev, [uploaded.id]: uploaded }));
                    resetSlidePicker();
                    return;
                }
                slide = elRes.data ?? { ...uploaded, docs_lightbox_id: editLightBox.id };
            }
            let removedOld = true;
            if (replacing) {
                const [delRes] = await removeElements([replacing]);
                removedOld = delRes?.status >= 200 && delRes?.status < 300;
            }
            if (editLightBox?.id) refreshObjInitLightBox(editLightBox.id);
            setImageElement(prev => {
                const next = { ...prev, [slide.id]: slide };
                if (replacing && removedOld) delete next[replacing.id];
                return next;
            });
            resetSlidePicker();
            setReplacingSlide(null);
            const saved = editLightBox?.id ? '' : ' Save the lightbox to keep it.';
            setShowToast({
                showToast: true,
                toastType: removedOld ? 'success' : 'danger',
                toastMessage: !removedOld ? `New image added, but the old slide ${replacing.number} couldn't be removed. Remove it from the slides below.`
                    : replacing ? `Slide ${replacing.number} replaced.${saved}` : `Slide added.${saved}`,
            });
        } finally {
            setAddingSlide(false);
        }
    };

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const displayElement = () => {
        switch (chosenType) {
            case 4:
                return <ImageSection
                    pickInPreview={true}
                    key={isSlideshow ? `slide-${slidePickerKey}` : (lbEditElement?.id || 'new')}
                    tool={tool}
                    handleImageUpload={handleImageUploadForLightbox}
                    uploadElement={handleImageUploadForLightbox}
                    slideCount={isSlideshow ? arrayOfImageElements.length : null}
                    editElement={isSlideshow ? {} : (lbEditElement?.id ? lbEditElement : editElement)}
                    handleSelectGifs={handleSelectGifsForLightbox}
                    errorState={errorState}
                />
            case 5:
                return <VideoSection
                    text={'Please add the embed code provided by youtube or vimeo. Link must contain http or https.'}
                    handleVideoUrlUpload={markDirty(handleElementUpload)}
                    videoLink={tool?.value?.video_link}
                    type={'video'}
                />
            case 8:
                return <VideoSection
                    text={'Please add the embed code provided by youtube. Link must contain http or https.'}
                    handleVideoUrlUpload={markDirty(handleElementUpload)}
                    videoLink={tool?.value?.video_link}
                    type={"audio"}
                />
            case 6:
                return <EmbedSection
                    handleEmbedUrlUpload={markDirty(handleElementUpload)}
                    siteUrl={tool?.value?.video_link}
                    preventScroll={tool?.value?.no_scroll}
                />
            case 7:
                return <TextEditorSection
                    handleTextChange={markDirty(handleTextChange)}
                    html_text={tool?.value?.html_text}
                />
            case 12:
                return <HtmlSourceSection
                    handleHtmlTextChange={markDirty(handleHtmlChange)}
                    html_text={tool?.value?.html_text}
                />
            default:
                return null;
        }
    }

    const usages = editLightBox?.usages ?? [];
    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const lbW = Number(lightBoxPayload?.width) || 0;
    const lbH = Number(lightBoxPayload?.height) || 0;
    const sizeFits = lbW > 0 && lbH > 0 ? lbW <= screenW && lbH <= screenH : null;
    const triggerChanged = !!editLightBox?.id && lightBoxPayload?.display !== editLightBox?.display;
    const triggerSummary = TRIGGERS.find((t) => t.value === lightBoxPayload?.display)?.badge;

    return (
        <div className="lb-setting-root">
            <div className="lb-name-row">
                <label className="lb-name-label" htmlFor="lb-name-input">Name</label>
                <CustomInput
                    id="lb-name-input"
                    ref={inputRef}
                    placeholder={"Please Enter Lightbox Name"}
                    value={lightBoxPayload?.name}
                    name={'name'}
                    onChange={handleChange}
                    required={true}
                    autoFocus={true}
                    type={'text'}
                    error={error?.fieldError}
                />
            </div>

            <SectionCard
                title="Content"
                variant="content"
                defaultOpen={true}
                errors={[error?.elementError, error?.contentError, error?.sizeError]}
            >
                <LightBoxElement
                    lightBoxPayload={lightBoxPayload}
                    chosenType={chosenType}
                    handleChange={handleChange}
                />
                {error?.elementError && <p className="text-danger">{error.elementError}</p>}
                {hasElement ? (
                    <div className="lb-content-editor">
                        {displayElement()}
                        {error?.contentError && <p className="text-danger">{error.contentError}</p>}
                        <PreviewSizeBlock
                            label="Lightbox"
                            fitBox
                            hideCanvas={[7, 12].includes(chosenType)}
                            embedSrc={[5, 8].includes(chosenType) ? tool?.value?.video_link || null
                                : chosenType === 6 && /^https:\/\/\S+$/i.test(tool?.value?.video_link || '') ? tool.value.video_link : null}
                            embedKind={{ 5: 'video', 8: 'audio' }[chosenType] || 'content'}
                            box={{ width: Number(lightBoxPayload?.width) || 0, height: Number(lightBoxPayload?.height) || 0 }}
                            icon={TYPE_ICONS[chosenType]}
                            src={!isImageMode ? null
                                : isSlideshow ? (hasPendingSlide ? tool?.value?.gif_link || null : replacingSlide?.file_url || replacingSlide?.value?.gif_link || null)
                                : (tool?.value?.gif_link || arrayOfImageElements[0]?.file_url || arrayOfImageElements[0]?.value?.gif_link || null)}
                            file={isImageMode && (!isSlideshow || hasPendingSlide) ? tool?.file : null}
                            onPickFile={isImageMode ? (e) => handleImageUploadForLightbox(e, 'upload') : undefined}
                            pickHint={!isSlideshow ? undefined
                                : replacingSlide ? (hasPendingSlide ? `Press Replace slide ${replacingSlide.number} to swap it in` : `Click to pick a new image for slide ${replacingSlide.number}`)
                                : 'Click to pick an image, then press Add to slideshow'}
                            tone={FRAME_TYPES.includes(chosenType) ? 'dark' : 'light'}
                            width={{ name: 'width', value: lightBoxPayload?.width ?? '', onChange: handleChange, onBlur: handleSizeBlur }}
                            height={{ name: 'height', value: lightBoxPayload?.height ?? '', onChange: handleChange, onBlur: handleSizeBlur }}
                            lock={{ id: 'lb-keep-proportions', name: 'keep_proportions', checked: !!lightBoxPayload?.keep_proportions, onChange: handleChange }}
                            presets={[...Object.keys(RATIOS), ...(isImageMode && naturalSize?.width && naturalSize?.height ? ['Fit content'] : [])].map((label) => ({ label, onClick: () => applySizePreset(label) }))}
                            fit={sizeFits == null ? null : { ok: sizeFits, text: sizeFits ? `Fits your screen (${screenW} × ${screenH})` : `Larger than your screen (${screenW} × ${screenH}); recipients see it scaled down` }}
                            error={error?.sizeError}
                        >
                            {isImageMode && (
                                <div className="pv-checks">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="slide_show"
                                            checked={!!lightBoxPayload?.slide_show}
                                            onChange={handleChange}
                                        />
                                        Slide show
                                    </label>
                                    {isSlideshow && (
                                        <label className="lb-slide-interval">
                                            Auto-advance every
                                            <input
                                                type="number"
                                                name="slide_interval"
                                                min="1"
                                                max="300"
                                                value={lightBoxPayload?.slide_interval ?? DEFAULT_SLIDE_INTERVAL}
                                                onChange={handleChange}
                                                onBlur={(e) => {
                                                    const n = Math.round(Number(e.target.value));
                                                    const clamped = !n || n < 1 ? DEFAULT_SLIDE_INTERVAL : Math.min(n, 300);
                                                    if (clamped !== lightBoxPayload?.slide_interval) {
                                                        handleChange({ target: { name: 'slide_interval', value: clamped, type: 'number' } });
                                                    }
                                                }}
                                            />
                                            sec
                                        </label>
                                    )}
                                </div>
                            )}
                        </PreviewSizeBlock>
                        {isSlideshow && (
                            <LightBoxSlideList
                                slides={arrayOfImageElements}
                                canAdd={hasPendingSlide}
                                adding={addingSlide}
                                onAdd={handleAddSlide}
                                replacing={replacingSlide}
                                onEdit={handleEditSlide}
                                onCancelReplace={() => { resetSlidePicker(); setReplacingSlide(null); }}
                            />
                        )}
                        {isImageMode && !isSlideshow && arrayOfImageElements?.length > 1 && (
                            <div className="lb-image-meta">
                                <ImageSlider arrayOfImageElements={arrayOfImageElements} showIcons={true} />
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="lb-muted mt-2 mb-0">Choose a content type to add its content and size.</p>
                )}
            </SectionCard>

            <SectionCard title="Trigger" variant="action" defaultOpen={!editLightBox?.id} summary={triggerSummary}>
                <LightBoxTrigger
                    lightBoxPayload={lightBoxPayload}
                    handleChange={handleChange}
                />
                {triggerChanged && usages.length > 0 && (
                    <div className="lb-warnline">
                        After saving, this lightbox will no longer open from: {usages.map(usageText).join(', ')}.
                    </div>
                )}
            </SectionCard>
            {!!editLightBox?.id && (
                <SectionCard title="Used in" variant="metadata" summary={usages.length ? `${usages.length} place${usages.length > 1 ? 's' : ''}` : 'Not used'}>
                    <LightBoxUsages usages={usages} createdBy={editLightBox?.created_user} updatedAt={editLightBox?.updated_at} />
                </SectionCard>
            )}

            <div className="lightbox-setting-lower-btn">
                <div className="lb-footer-row">
                    <span className={`elem-modal-dirty${dirty ? '' : ' clean'}`}>
                        {dirty ? <><FontAwesomeIcon icon={faCircle} className="elem-modal-dirty-dot" /> Unsaved changes</> : ''}
                    </span>
                    <div className="d-flex">
                        <div className="me-2">
                            <Button
                                classList='success'
                                addonClass='elem-save-outline'
                                text='Save'
                                disabled={saving}
                                onClick={() => handleLightBox('save')}
                            />
                        </div>
                        <Button
                            classList='success'
                            addonClass='elem-save'
                            text='Save & Close'
                            disabled={saving}
                            onClick={() => handleLightBox('close')}
                        />
                    </div>
                </div>
                <div className="republish-banner">
                    After saving, republish this KDF for recipients to experience your changes.
                </div>
            </div>
            {showSizeWarning && (
                <LightBoxExtendWarningModal
                    openModal={showSizeWarning}
                    closeModal={setShowSizeWarning}
                    onConfirm={handleConfirmOversizedLightbox}
                    onRevert={handleRevertOversizedLightbox}
                />
            )}
        </div>
    )
}
export default LightBoxSetting
